import { ChartFrequency, IComposableChartsWidget } from 'wisestep-api';

import { getCandidateJobStatus } from '../../../../analyticsPage/helper/postgreDrawerChartsList';
import { generalTimePeriodList } from '../../../../analyticsPage/helper/timePeriodList';
import { IBusinessData } from '../../../../crmSettings/customizeStageStatus/helper';
import BusinessDefinitionTablePostgre from '../../../BaseCharts/businessDefinition/new/BusinessDefinitionTablePostgre';
import JobsTablePostgre from '../../../BaseCharts/jobs/postgre/JobsTablePostgre';
import {
  SubmissionsBarChartPostgre,
  SubmissionsMetricChartPostgre,
} from '../../../BaseCharts/submissions/new';
import { ChartScope } from '../../../types';
import { JobsWidget, Widget } from '../../helper';

const getChartWidgetByTimeAndScope = (
  timePeriod: { id: string; label: string; filterDate: string },
  scope: ChartScope,
  businessDefinition: IBusinessData,
  frequency = getChartFrequency(timePeriod.id)
): Partial<Record<Widget, IComposableChartsWidget>> => {
  const timePeriodId = timePeriod.id;
  const filterDate = timePeriod.filterDate;
  const businessDefinitionName = businessDefinition.businessDefinitionName;
  const defaultQuery = {
    type: 'SUBMISSION_COUNT',
    category: 'businessDefinition',
    businessDefinitionId: businessDefinition.id,
    filters: getBusinessDefinitionFilters(businessDefinition),
    filterDate,
    sort: [],
    frequency,
    range: timePeriodId,
    scope,
  };
  const freqInRegularCase = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();

  const widgetList = {
    [Widget.METRIC_TOTAL]: {
      name: `${businessDefinitionName}`,
      filters: {},
      type: `metric_submissions_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <SubmissionsMetricChartPostgre
          query={{ ...defaultQuery, type: 'SUBMISSION_COUNT' }}
          title={`${businessDefinitionName}`}
          widgetName={Widget.METRIC_TOTAL}
        />
      ),
    },
    [Widget.BAR_TOTAL]: {
      name: `Trends - ${businessDefinitionName} - ${freqInRegularCase}`,
      filters: {},
      type: `barchart_submissions_${timePeriodId}_${filterDate}_${scope}_${frequency}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <SubmissionsBarChartPostgre
          query={{ ...defaultQuery, type: 'SUBMISSION_COUNT_BAR' }}
          title={`Trends - ${businessDefinitionName} - ${freqInRegularCase}`}
          widgetName={Widget.BAR_TOTAL}
        />
      ),
    },
    [Widget.STACKEDBAR_CLIENTS]: {
      name: `Client Wise - ${businessDefinitionName} - ${freqInRegularCase}`,
      filters: {},
      type: `stackedbarchart_submissions_${timePeriodId}_${filterDate}_${scope}_${frequency}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <SubmissionsBarChartPostgre
          query={{ ...defaultQuery, type: 'STACKED_CLIENTS' }}
          title={`Client Wise - ${businessDefinitionName} - ${freqInRegularCase}`}
          widgetName={Widget.STACKEDBAR_CLIENTS}
        />
      ),
    },
    [Widget.TABLE_BY_USERS]: {
      name: `${businessDefinitionName} By Recruiter`,
      filters: {},
      type: `table_businessdefinition_recruiter_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'RECRUITER_NAME' }}
          title={`${businessDefinitionName} By Recruiter`}
          widgetName={Widget.TABLE_BY_USERS}
        />
      ),
    },
    [Widget.TABLE_TO_CLIENTS]: {
      name: `${businessDefinitionName} By Client`,
      filters: {},
      type: `table_businessdefinition_client_split_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'CLIENTS' }}
          title={`${businessDefinitionName} By Client`}
          widgetName={Widget.TABLE_TO_CLIENTS}
        />
      ),
    },
    [Widget.TABLE_TO_AM]: {
      name: `${businessDefinitionName} By Account Manager`,
      filters: {},
      type: `table_businessdefinition_account_manager_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'ACCOUNT_MANAGER' }}
          title={`${businessDefinitionName} By Account Manager`}
          widgetName={Widget.TABLE_TO_AM}
        />
      ),
    },
    [Widget.TABLE_CLIENTWISE]: {
      name: `${businessDefinitionName} By Client and Client Contact`,
      filters: {},
      type: `table_businessdefinition_client_wise_submission_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'CLIENTS_AND_CONTACTS' }}
          title={`${businessDefinitionName} By Client and Client Contact`}
          widgetName={Widget.TABLE_CLIENTWISE}
        />
      ),
    },
    [Widget.TABLE_BY_CLIENT_SPOC]: {
      name: `${businessDefinitionName} By Client Contact`,
      filters: {},
      type: `table_businessdefinition_submission_client_spoc_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'CLIENT_SPOC' }}
          title={`${businessDefinitionName} By Client Contact`}
          widgetName={Widget.TABLE_BY_CLIENT_SPOC}
        />
      ),
    },
    [Widget.TABLE_JOB_TITLE]: {
      name: `Submissions by Job Title - ${businessDefinitionName}`,
      filters: {},
      type: `table_businessdefinition_submission_job_title_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'JOB_TITLE' }}
          title={`Submissions by Job Title - ${businessDefinitionName}`}
          widgetName={Widget.TABLE_JOB_TITLE}
        />
      ),
    },
    [Widget.TABLE_STAGE_STATE]: {
      name: `Stage Status of ${businessDefinitionName}`,
      filters: {},
      type: `table_businessdefinition_stage_state_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'STAGE_STATE' }}
          title={`Stage Status of ${businessDefinitionName}`}
          widgetName={Widget.TABLE_STAGE_STATE}
        />
      ),
    },
    [Widget.TABLE_CANDIDATE_WISE_STAGE_DETAILS]: {
      name: `Candidate Wise ${businessDefinitionName} (joining date)`,
      filters: {},
      type: `table_businessdefinition_candidate_wise_stage_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'CANDIDATE_WISE_STAGE_DETAILS' }}
          title={`Candidate Wise ${businessDefinitionName} (joining date)`}
          widgetName={Widget.TABLE_CANDIDATE_WISE_STAGE_DETAILS}
          widgetType={`table_businessdefinition_candidate_wise_stage_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`}
        />
      ),
    },
    [Widget.TABLE_CANDIDATE_STAGE_DETAILS_WJD]: {
      name: `Candidate Wise ${businessDefinitionName}`,
      filters: {},
      type: `table_businessdefinition_wjd_candidate_wise_stage_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <BusinessDefinitionTablePostgre
          query={{ ...defaultQuery, type: 'CANDIDATE_WISE_STAGE_DETAILS' }}
          title={`Candidate Wise ${businessDefinitionName}`}
          widgetName={Widget.TABLE_CANDIDATE_STAGE_DETAILS_WJD}
        />
      ),
    },
    [Widget.TABLE_JOB_SUBMISSION_COUNT]: {
      name: `${businessDefinitionName} - Jobs by Submissions Count`,
      filters: {},
      type: `table_businessdefinition_jobs_by_submission_${timePeriodId}_${filterDate}_${scope}_${businessDefinitionName}_postgre`,
      category: 'businessDefinition',
      component: (
        <JobsTablePostgre
          query={{ ...defaultQuery, type: 'submission-count-table' }}
          title={`${businessDefinitionName} - Jobs by Submissions Count`}
          widgetName={JobsWidget.TABLE_SUBMISSION_COUNT}
        />
      ),
    },
  };

  return widgetList;
};

const getBusinessDefinitionFilters = (businessDefinition: IBusinessData) => {
  return [
    {
      filterName: 'BUSINESS_DEFINITION',
      fields: { stageStatus: businessDefinition.stageStatusData },
    },
    {
      filterName: 'JOB_STATUS',
      values: [getCandidateJobStatus(businessDefinition).toUpperCase()],
    },
  ];
};

const getChartFrequency = (timePeriod: string): ChartFrequency => {
  switch (timePeriod) {
    case '1H':
    case '8H':
    case 'TODAY':
    case '24H':
      return ChartFrequency.HOURLY;
    case 'THIS_WEEK':
    case '7D':
      return ChartFrequency.DAILY;
    case '30D':
    case 'MONTH_TILL_DATE':
      return ChartFrequency.WEEKLY;
    case 'CURRENT_QUARTER':
    case 'LAST_QUARTER':
    case 'YEAR_TILL_DATE':
    case 'LAST_YEAR':
      return ChartFrequency.MONTHLY;
    default:
      return ChartFrequency.HOURLY;
  }
};

export const postgreBusinessDefinitionKeys = (
  timePeriod: { id: string; label: string; filterDate: string },
  scope: ChartScope,
  allBusinessDefinition: IBusinessData[] = []
): IComposableChartsWidget[] => {
  return allBusinessDefinition.flatMap((businessDefinition) => {
    const defaultWidgets = Object.values(
      getChartWidgetByTimeAndScope(timePeriod, scope, businessDefinition)
    );
    const weeklyCharts = getChartWidgetByTimeAndScope(
      timePeriod,
      scope,
      businessDefinition,
      ChartFrequency.WEEKLY
    );
    const additionalWidgets = {
      ['CURRENT_QUARTER']: [
        weeklyCharts[Widget.BAR_TOTAL],
        weeklyCharts[Widget.STACKEDBAR_CLIENTS],
      ],
      ['LAST_QUARTER']: [weeklyCharts[Widget.BAR_TOTAL], weeklyCharts[Widget.STACKEDBAR_CLIENTS]],
    };
    return [...defaultWidgets, ...(additionalWidgets[timePeriod.id] ?? [])];
  });
};

export const businessDefinitionTimePeriodList = [
  ...[...generalTimePeriodList, { id: '365D', label: 'Last 365 Days' }].map((timePeriod) => ({
    ...timePeriod,
    filterDate: 'created_on',
  })),
  ...[...generalTimePeriodList, { id: '365D', label: 'Last 365 Days' }].map((timePeriod) => ({
    ...timePeriod,
    label: `${timePeriod.label}-Updated On`,
    filterDate: 'modified_on',
  })),
];
